import {
  Box,
  Typography,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@material-ui/core';
import {
  KeyboardArrowDown,
  Link as LinkIcon,
  AddCircleOutline,
  RotateLeft,
  Input,
  DescriptionOutlined,
  SwapHoriz,
} from '@material-ui/icons';
import { Button } from '@roc/ui';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react';
import { Document } from '@roc/feature-types';
import { useRef, useState } from 'react';
import { AppraisalDetailsHeader } from './appraisalDetailsHeader';
import { AppraisalQuestionnaireModal } from './appraisalQuestionnaireModal';
import { AppraisalPaymentLinkModal } from './appraisalPaymentLinkModal';
import { makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { AppraisalDetailsCard } from './appraisalDetailsCard';
import { AppraisalPaymentsTab } from './appraisalPaymentsTab';
import { AppraisalDetailsTab } from './appraisalDetailsTab';
import {
  AppraisalDetailsV2Store,
  AppraisalReceiptData,
} from '../../../../stores/documentForms/appraisalDetailsV2/appraisalDetailsV2Store';
import { ExtraPaymentLinkDialog } from './actions/extraPaymentLink';
import {
  AppraisalAction,
  ActionContext,
  GLOBAL_ACTIONS,
  GLOBAL_SUB_ACTIONS,
} from '../utils/appraisalDetailsV2Constants';
import { useStore } from '@roc/client-portal-shared/hooks';
import { useUserStore } from '@roc/feature-app-core';
import { ValuationReorderDialog } from './actions/valuationReorder';
import { ValuationRecertificationDialog } from './actions/valuationRecertification';
import { ReconsiderationOfValueDialog } from './actions/valuationReconsiderationOfValue';
import { ValuationTransferDialog } from './actions/valuationTransfer';
import { AddAppraisalDialog } from './actions/addAppraisal';
import {
  APPRAISAL_RECEIPT_PREVIEW_WIDTH,
  AppraisalPaymentReceiptPreview,
} from './appraisalPaymentReceiptPreview';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

// Re-export with old spelling for backwards compatibility
export { AppraisalQuestionnaireModal as AppraisalQuestionaireModal } from './appraisalQuestionnaireModal';
export { AppraisalPaymentLinkModal } from './appraisalPaymentLinkModal';

type Props = {
  open?: boolean;
  onClose?: () => void;
  loan: any;
  document: Document;
};

const useAppraisalDetailsStyles = makeStyles((theme: Theme) => ({
  promptCard: {
    padding: '40px',
    textAlign: 'center',
    marginTop: '24px',
  },
  promptTitle: {
    fontSize: '20px',
    fontWeight: 600,
    marginBottom: '16px',
  },
  getStartedButton: {
    fontSize: '16px',
    padding: '10px 40px',
    textTransform: 'none',
  },
  actionsButton: {
    textTransform: 'none',
    padding: '8px 24px',
  },
  confirmDialogContent: {
    minWidth: '420px',
  },
  confirmMessage: {
    fontSize: '16px',
    lineHeight: 1.5,
  },
  cancelButton: {
    textTransform: 'none',
  },
  confirmButton: {
    textTransform: 'none',
  },
  hiddenReceiptContainer: {
    position: 'fixed',
    left: '-200vw',
    top: 0,
    width: `${APPRAISAL_RECEIPT_PREVIEW_WIDTH}px`,
    pointerEvents: 'none',
    zIndex: -1,
  },
}));

const APPRAISAL_PAYMENT_LOAN = 'Appraisal_Loan';
const PDF_PAGE_MARGIN_PT = 36;
const PDF_PAGE_WIDTH_PT = 612;
const PDF_PAGE_HEIGHT_PT = 792;

const downloadElementAsPdf = async (element: HTMLElement, fileName: string) => {
  const canvas = await html2canvas(element, {
    backgroundColor: '#FFFFFF',
    scale: 2,
    useCORS: true,
    scrollX: 0,
    scrollY: 0,
    width: element.scrollWidth,
    height: element.scrollHeight,
    windowWidth: element.scrollWidth,
    windowHeight: element.scrollHeight,
  });
  const pdf = new jsPDF('portrait', 'pt', 'letter');
  const contentWidth = PDF_PAGE_WIDTH_PT - PDF_PAGE_MARGIN_PT * 2;
  const contentHeight = PDF_PAGE_HEIGHT_PT - PDF_PAGE_MARGIN_PT * 2;
  const scaledImageHeight = canvas.height * (contentWidth / canvas.width);
  const imageData = canvas.toDataURL('image/png');
  let remainingHeight = scaledImageHeight;
  let positionY = PDF_PAGE_MARGIN_PT;

  pdf.addImage(
    imageData,
    'PNG',
    PDF_PAGE_MARGIN_PT,
    positionY,
    contentWidth,
    scaledImageHeight,
    undefined,
    'FAST'
  );

  remainingHeight -= contentHeight;

  while (remainingHeight > 0) {
    pdf.addPage('letter', 'portrait');
    positionY = PDF_PAGE_MARGIN_PT - (scaledImageHeight - remainingHeight);
    pdf.addImage(
      imageData,
      'PNG',
      PDF_PAGE_MARGIN_PT,
      positionY,
      contentWidth,
      scaledImageHeight,
      undefined,
      'FAST'
    );
    remainingHeight -= contentHeight;
  }

  pdf.save(fileName);
};

type ConfirmResetValuationsModalProps = {
  open: boolean;
  loanId?: string | number;
  onClose: () => void;
  onConfirm: () => void;
};

const ConfirmResetValuationsModal = ({
  open,
  loanId,
  onClose,
  onConfirm,
}: ConfirmResetValuationsModalProps) => {
  const classes = useAppraisalDetailsStyles();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Confirm Reset</DialogTitle>
      <DialogContent className={classes.confirmDialogContent}>
        <Typography className={classes.confirmMessage}>
          Are you sure you want to reset the valuation? This will permanently
          erase all progress on this loan. Please confirm.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          color="secondary"
          onClick={onClose}
          className={classes.cancelButton}
          testId="cancel-reset-valuations-button"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onConfirm}
          className={classes.confirmButton}
          testId="confirm-reset-valuations-button"
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const AppraisalDetails = observer(
  ({ store, loanId, lenderId }: { store: AppraisalDetailsV2Store; loanId?: number, lenderId?: number }) => {
    const classes = useAppraisalDetailsStyles();
    const { globalStore } = useStore();
    const [selectedTab, setSelectedTab] = useState(AppraisalTab.APPRAISALS);
    const [showPaymentLinkModal, setShowPaymentLinkModal] = useState(false);
    const [
      frontendReceiptData,
      setFrontendReceiptData,
    ] = useState<AppraisalReceiptData | null>(null);
    const receiptRef = useRef<HTMLDivElement | null>(null);

    const handleDownloadReceiptFrontend = async (referenceId: string) => {
      const receiptData = await flowResult(
        store.fetchPaymentReceiptData(referenceId)
      );

      if (!receiptData) {
        return;
      }

      setFrontendReceiptData(receiptData);

      requestAnimationFrame(() => {
        requestAnimationFrame(async () => {
          const receiptElement = receiptRef.current;

          if (!receiptElement) {
            globalStore.notificationStore.showErrorNotification({
              message: 'Unable to prepare the frontend receipt preview.',
            });
            setFrontendReceiptData(null);
            return;
          }

          try {
            await downloadElementAsPdf(
              receiptElement,
              `valuation-payment-receipt-fe-${receiptData.referenceId}.pdf`
            );
          } catch (error) {
            globalStore.notificationStore.showErrorNotification({
              message: 'Error while generating the frontend receipt.',
            });
          } finally {
            setFrontendReceiptData(null);
          }
        });
      });
    };

    return (
      <>
        {/* Tabs and Actions */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <AppraisalTabs
            tab={selectedTab}
            setTab={setSelectedTab}
            pendingPayments={store.pendingPaymentsCount}
          />
          <Box display="flex" alignItems="center" style={{ gap: '8px' }}>
            {store.pendingPaymentsCount > 0 && (
              <Button
                variant="contained"
                color="primary"
                testId="get-payment-link"
                onClick={() => setShowPaymentLinkModal(true)}
                startIcon={<LinkIcon />}
              >
                Get Payment Link
              </Button>
            )}
            <AppraisalActionsButton store={store} loanId={loanId} />
          </Box>
        </Box>
        <AppraisalPaymentLinkModal
          store={store}
          open={showPaymentLinkModal}
          onClose={() => setShowPaymentLinkModal(false)}
          loanId={loanId}
          lenderId={lenderId}
        />
        {selectedTab === AppraisalTab.APPRAISALS && (
          <AppraisalDetailsCard>
            <AppraisalDetailsTab store={store} loanId={loanId} />
          </AppraisalDetailsCard>
        )}
        {selectedTab === AppraisalTab.PAYMENTS && (
          <AppraisalDetailsCard>
            <AppraisalPaymentsTab
              totalPaid={
                store.totalPaid != null
                  ? `$${store.totalPaid.toLocaleString()}`
                  : '$0'
              }
              pendingPayments={
                store.pendingPayments != null
                  ? `$${store.pendingPayments.toLocaleString()}`
                  : '$0'
              }
              lastPayment={store.lastPaymentDate ?? '-'}
              payments={store.payments}
              store={store}
              onDownloadReceipt={referenceId =>
                handleDownloadReceiptFrontend(referenceId)
              }
              onDownloadPaymentAuthorization={referenceId =>
                store.downloadPaymentAuthorization(referenceId)
              }
            />
          </AppraisalDetailsCard>
        )}
        {frontendReceiptData && (
          <Box className={classes.hiddenReceiptContainer}>
            <div ref={receiptRef}>
              <AppraisalPaymentReceiptPreview
                referenceId={frontendReceiptData.referenceId}
                status={frontendReceiptData.status}
                amount={frontendReceiptData.amount}
                paymentDate={frontendReceiptData.paymentDate ?? '-'}
                paymentType="Credit Card"
                cardHolderName={
                  frontendReceiptData.cardHolderName ?? store.appraisal.borrower
                }
                cardHolderEmail={
                  frontendReceiptData.cardHolderEmail ??
                  store.borrowerEmail ??
                  null
                }
                loanId={
                  frontendReceiptData.loanId ??
                  String(store.appraisal.loanNumber || '-')
                }
                purpose={frontendReceiptData.purpose ?? APPRAISAL_PAYMENT_LOAN}
              />
            </div>
          </Box>
        )}
      </>
    );
  }
);

export const QuestionnairePrompt = ({
  store,
  loanId,
}: {
  store: AppraisalDetailsV2Store;
  loanId: number;
}) => {
  const classes = useAppraisalDetailsStyles();
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  return (
    <AppraisalDetailsCard className={classes.promptCard}>
      <Typography className={classes.promptTitle}>
        Please Complete the Valuation Questionnaire
      </Typography>
      <Button
        className={classes.getStartedButton}
        variant="contained"
        color="primary"
        onClick={() => setShowQuestionnaire(true)}
        testId="get-started-button"
      >
        Get Started
      </Button>
      <AppraisalQuestionnaireModal
        open={showQuestionnaire}
        onClose={() => setShowQuestionnaire(false)}
        onSuccess={() => store.fetchAppraisalDetails(loanId)}
      />
    </AppraisalDetailsCard>
  );
};

const actionIcons: Partial<Record<AppraisalAction, React.ReactNode>> = {
  [AppraisalAction.EXTRA_PAYMENT_LINK]: (
    <AddCircleOutline style={{ marginRight: '8px' }} />
  ),
  [AppraisalAction.RESET]: <RotateLeft style={{ marginRight: '8px' }} />,
  [AppraisalAction.VALUATION_REORDER]: <Input style={{ marginRight: '8px' }} />,
  [AppraisalAction.VALUATION_RECERTIFICATION]: (
    <DescriptionOutlined style={{ marginRight: '8px' }} />
  ),
  [AppraisalAction.VALUATION_TRANSFER]: (
    <SwapHoriz style={{ marginRight: '8px' }} />
  ),
  [AppraisalAction.ADD_APPRAISAL]: (
    <AddCircleOutline style={{ marginRight: '8px' }} />
  ),
};

const AppraisalActionsButton = ({
  store,
  loanId,
}: {
  store: AppraisalDetailsV2Store;
  loanId: number;
}) => {
  const classes = useAppraisalDetailsStyles();
  const { globalStore } = useStore();
  const { userStore } = useUserStore();
  const [curentAction, setCurrentAction] = useState<AppraisalAction | null>(
    null
  );
  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [refetchOnModalClose, setRefetchOnModalClose] = useState(false);

  const ctx: ActionContext = {
    isInternal: globalStore.isInternalPortal,
    isTechUser: userStore.isTechUser,
    isTamariskUser: userStore.isTamariskUser,
    isUnderwriterRole:
      userStore.isUnderwriter ||
      userStore.isUnderwriterProduct ||
      userStore.isUnderwriterCommittee,
    isUnderwriterProduct: userStore.isUnderwriterProduct,
    isDecisionCommittee: userStore.isDecisionCommittee,
    hasPendingPayment: store.pendingPaymentsCount > 0,
    hasCompletedValuation: store.valuations.some(v =>
      ['completed', 'approved'].includes(v.valuationStatus.toLowerCase())
    ),
    loanSubType: store.appraisal?.loanType,
    selectedAmcId: store.loanStore.loanDetails?.selectedAmcId,
    canResetValuations: store.canResetValuations,
  };
  const visibleActions = GLOBAL_ACTIONS.filter(a => a.isVisible(ctx));

  if (visibleActions.length === 0) return null;

  const canLinkOrder = GLOBAL_SUB_ACTIONS.find(
    a => a.key === AppraisalAction.LINK_ORDER
  )?.isVisible(ctx) ?? false;
  const canOrderFinalInspection = GLOBAL_SUB_ACTIONS.find(
    a => a.key === AppraisalAction.PUSH_1004D_ORDER
  )?.isVisible(ctx) ?? false;
  const canPushNewOrder = GLOBAL_SUB_ACTIONS.find(
    a => a.key === AppraisalAction.PUSH_NEW_ORDER
  )?.isVisible(ctx) ?? false;

  const handleActionsClick = (event: React.MouseEvent<HTMLElement>) => {
    setActionsAnchorEl(event.currentTarget);
  };

  const handleActionsClose = () => {
    setActionsAnchorEl(null);
  };

  const handleActionClick = (action: AppraisalAction) => {
    setCurrentAction(action);
    handleActionsClose();
  };

  const resetSucceeded = async () => {
    try {
      await flowResult(store.resetValuationsByLoanId(loanId));
      return true;
    } catch (e) {
      return false;
    }
  };

  const showTheResetResult = async () => {
    try {
      store.reset();
      await flowResult(store.fetchAppraisalDetails(loanId));
      await flowResult(store.fetchResetCapability(loanId));
    } finally {
      dismissTheDialog();
    }
  };

  const dismissTheDialog = () => setCurrentAction(null);

  const handleConfirmResetValuations = async () => {
    if (await resetSucceeded()) {
      await showTheResetResult();
    }
  };

  return (
    <>
      <Button
        className={classes.actionsButton}
        variant="contained"
        color="primary"
        testId="appraisal-actions"
        onClick={handleActionsClick}
        endIcon={<KeyboardArrowDown />}
      >
        Valuation Actions
      </Button>
      <Menu
        anchorEl={actionsAnchorEl}
        open={Boolean(actionsAnchorEl)}
        onClose={handleActionsClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {visibleActions.map(action => (
          <MenuItem
            key={action.key}
            onClick={() => handleActionClick(action.key)}
          >
            {actionIcons[action.key]}
            {action.label}
          </MenuItem>
        ))}
      </Menu>
      <ExtraPaymentLinkDialog
        store={store}
        open={curentAction === AppraisalAction.EXTRA_PAYMENT_LINK}
        onClose={() => setCurrentAction(null)}
        onSuccess={(link: string) => {
          store.setPaymentLink(link);
          setRefetchOnModalClose(true);
          setCurrentAction(AppraisalAction.GET_PAYMENT_LINK);
        }}
      />
      <AppraisalPaymentLinkModal
        store={store}
        open={curentAction === AppraisalAction.GET_PAYMENT_LINK}
        onClose={() => {
          setCurrentAction(null);
          if (refetchOnModalClose) {
            setRefetchOnModalClose(false);
            store.fetchAppraisalDetails(loanId);
          }
        }}
        loanId={loanId}
      />
      <ConfirmResetValuationsModal
        open={curentAction === AppraisalAction.RESET}
        onClose={() => setCurrentAction(null)}
        onConfirm={handleConfirmResetValuations}
        loanId={loanId}
      />
      <ValuationReorderDialog
        store={store}
        open={curentAction === AppraisalAction.VALUATION_REORDER}
        onClose={() => setCurrentAction(null)}
      />
      <ValuationRecertificationDialog
        store={store}
        open={curentAction === AppraisalAction.VALUATION_RECERTIFICATION}
        onClose={() => setCurrentAction(null)}
      />
      <ValuationTransferDialog
        store={store}
        open={curentAction === AppraisalAction.VALUATION_TRANSFER}
        onClose={() => setCurrentAction(null)}
      />
      <AddAppraisalDialog
        store={store}
        open={curentAction === AppraisalAction.ADD_APPRAISAL}
        onClose={() => setCurrentAction(null)}
        loanId={loanId}
        canLinkOrder={canLinkOrder}
        canOrderFinalInspection={canOrderFinalInspection}
        canPushNewOrder={canPushNewOrder}
        selectedAmcId={ctx.selectedAmcId}
        onSuccess={() => flowResult(store.fetchAppraisalDetails(loanId))}
      />
    </>
  );
};

const useTabStyles = makeStyles({
  tabs: {
    padding: '4px',
    background: 'rgb(234 234 234)',
    borderRadius: '9999px',
    justifyContent: 'flex-start',
    alignItems: 'center',
    display: 'flex',
  },
  tab: {
    height: '48px',
    paddingLeft: '32px',
    paddingRight: '32px',
    borderRadius: '9999px',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
    display: 'flex',
    cursor: 'pointer',
    flex: '1 1 0',
    textAlign: 'center',
    fontSize: '16px',
    lineHeight: '16px',
    wordWrap: 'break-word',
    color: 'var(--muted-muted-foreground, #52525B)',
    fontWeight: 400,
  },
  active: {
    background: 'var(--secondary-secondary, white)',
    boxShadow: '8px 8px 12px rgba(69, 69, 127, 0.04)',
    paddingLeft: '24px',
    paddingRight: '24px',
    flex: 'none',
    color: 'var(--base-base-foreground, #000007)',
    fontWeight: 700,
  },
  redDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: 'red',
    display: 'inline-block',
    verticalAlign: 'top',
  },
});

export enum AppraisalTab {
  APPRAISALS,
  PAYMENTS,
}

export const AppraisalTabs = observer(({ tab, setTab, pendingPayments }) => {
  const classes = useTabStyles();
  const tabs = [
    { label: 'Valuations', value: AppraisalTab.APPRAISALS },
    {
      label: 'Payments',
      value: AppraisalTab.PAYMENTS,
      pending: pendingPayments,
    },
  ];
  return (
    <div className={classes.tabs}>
      {tabs.map(t => (
        <div
          className={clsx(classes.tab, tab === t.value && classes.active)}
          onClick={() => setTab(t.value)}
        >
          <div>
            {t.label}
            {t.pending > 0 && (
              <>
                {' '}
                ({t.pending}) <div className={classes.redDot} />
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
});
